class ShopifyAuthModal {
  constructor(options) {
    if (ShopifyAuthModal.instance) {
      return ShopifyAuthModal.instance;
    }

    this.csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
    this.base = 'https://support.sokkat-alteeb.com/shopify-auth';
    this.modal = document.getElementById('shopify-auth-modal');
    this.form = this.modal?.querySelector('.shopify-auth-form');
    this.currentChannel = 'phone';
    this.countryCode = '966';
    this.flowData = null;
    this.returnTo = (options && options.returnTo) ? options.returnTo : (window.ShopifyAuthConfig?.returnTo || window.location.href);
    this.source = (options && options.source) ? options.source : (window.ShopifyAuthConfig?.source || 'header');
    this.resendCooldown = 60;
    this.resendTimer = null;
    this.resendEndsAt = null;

    // Cloudflare Turnstile (bot check before any SMS is sent).
    this.turnstileSiteKey = '0x4AAAAAAFu_WIxpxjY2NvUe';
    this.turnstileReady = null;
    this.turnstileWidgetId = null;

    this.isStarting = false;
    this.isSubmitting = false;
    this.isResending = false;
    this.eventsBound = false;

    if (!this.modal || !this.form) return;

    this.form.querySelector('input[name="shopify_auth_return_to"]').value = this.returnTo;
    this.bindEvents();
    this.initCountryCodes();

    ShopifyAuthModal.instance = this;
  }

  bindEvents() {
    if (this.eventsBound) return;
    this.eventsBound = true;

    const tabs = this.modal.querySelectorAll('.shopify-auth-tab');
    tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        this.switchChannel(tab.getAttribute('data-channel'));
      });
    });

    this.modal.querySelector('.shopify-auth-modal__close')?.addEventListener('click', () => this.hide());
    this.modal.querySelector('.shopify-auth-modal__backdrop')?.addEventListener('click', () => this.hide());

    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      if (this.flowData) {
        this.submitOtp();
      } else {
        this.startFlow();
      }
    });

    this.modal.querySelector('.shopify-auth-resend')?.addEventListener('click', (e) => {
      e.preventDefault();
      this.resendOtp();
    });

    const phoneInput = this.form.querySelector('input[name="shopify_auth_value"]');
    phoneInput?.addEventListener('input', (e) => {
      e.target.value = this.normalizeDigits(e.target.value).replace(/[^\d]/g, '');
    });

    const secondaryInput = this.form.querySelector('input[name="secondary_value"]');
    secondaryInput?.addEventListener('input', (e) => {
      if (e.target.type === 'text') {
        e.target.value = this.normalizeDigits(e.target.value).replace(/[^\d]/g, '');
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.hide();
    });
  }

  initCountryCodes() {
    const wrap = this.modal.querySelector('.shopify-auth-phone-code');
    const toggle = wrap?.querySelector('.shopify-auth-phone-code__toggle');
    const list = wrap?.querySelector('.shopify-auth-phone-code__list');
    // Missing markup must not stop the modal from opening.
    if (!wrap || !toggle || !list) return;


    // ISO + dial code.
    const codes = 'SA966,AE971,KW965,BH973,QA974,OM968,EG20,JO962,IQ964,LB961,SY963,PS970,YE967,SD249,LY218,TN216,DZ213,MA212,MR222,SO252,DJ253,KM269,TR90,IR98,PK92,IN91,BD880,LK94,NP977,AF93,ID62,MY60,PH63,SG65,TH66,VN84,CN86,HK852,TW886,JP81,KR82,GB44,IE353,FR33,DE49,IT39,ES34,PT351,NL31,BE32,LU352,CH41,AT43,SE46,NO47,DK45,FI358,IS354,PL48,CZ420,SK421,HU36,RO40,BG359,GR30,CY357,MT356,HR385,SI386,RS381,BA387,ME382,MK389,AL355,UA380,BY375,MD373,LT370,LV371,EE372,RU7,KZ7,UZ998,TM993,TJ992,KG996,AZ994,GE995,AM374,US1,CA1,MX52,BR55,AR54,CL56,CO57,PE51,VE58,EC593,UY598,PY595,BO591,AU61,NZ64,ZA27,NG234,KE254,ET251,GH233,TZ255,UG256,SN221,CI225,CM237,ER291,SS211,TD235,NE227,ML223,ZW263,ZM260,MZ258,AO244,MV960,BN673,KH855,LA856,MM95,MN976'.split(',');
    const countries = codes
      .map(c => ({ iso: c.slice(0, 2), dial: c.slice(2) }))
      .sort((a, b) => a.dial - b.dial);

    // Flag emoji from the ISO letters (S,A → 🇸🇦).
    const flag = (iso) => String.fromCodePoint(...[...iso].map(ch => 0x1F1E6 + ch.charCodeAt(0) - 65));

    list.innerHTML = countries.map(c => `<button type="button" role="option" data-iso="${c.iso}">${flag(c.iso)} +${c.dial}</button>`).join('');

    const setCountry = (iso) => {
      const country = countries.find(c => c.iso === iso);
      if (!country) return;
      this.countryCode = country.dial;
      toggle.textContent = `${flag(iso)} +${country.dial}`;
      list.querySelector('.is-selected')?.classList.remove('is-selected');
      list.querySelector(`[data-iso="${iso}"]`)?.classList.add('is-selected');
    };

    const close = () => {
      list.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
    };

    toggle.addEventListener('click', () => {
      list.hidden = !list.hidden;
      toggle.setAttribute('aria-expanded', String(!list.hidden));
      const selected = list.querySelector('.is-selected');
      if (!list.hidden && selected) {
        list.scrollTop = selected.offsetTop - list.clientHeight / 2;
      }
    });

    list.addEventListener('click', (e) => {
      const option = e.target.closest('[data-iso]');
      if (!option) return;
      setCountry(option.dataset.iso);
      close();
    });

    document.addEventListener('click', (e) => {
      if (!wrap.contains(e.target)) close();
    });

    setCountry('SA');
    // Store's market country first, then the visitor's IP country from Shopify.
    setCountry(wrap.dataset.default);
    fetch('/browsing_context_suggestions.json')
      .then(r => r.json())
      .then(data => setCountry(data.detected_values?.country?.handle))
      .catch(() => {});
  }

  show(options = {}) {
    if (options.returnTo) {
      this.returnTo = options.returnTo;
      this.form.querySelector('input[name="shopify_auth_return_to"]').value = this.returnTo;
    } else {
      this.returnTo = window.location.href;
      this.form.querySelector('input[name="shopify_auth_return_to"]').value = this.returnTo;
    }

    if (options.source) {
      this.source = options.source;
    }

    this.modal.style.display = 'block';
    document.body.classList.add('shopify-auth-modal-open');
    document.body.style.overflow = 'hidden';

    // Render while the modal is visible so a Turnstile checkbox can show if Cloudflare asks for one.
    this.renderTurnstile();
  }

  hide() {
    this.modal.style.display = 'none';
    document.body.classList.remove('shopify-auth-modal-open');
    document.body.style.overflow = '';
  }

  switchChannel(channel) {
    this.currentChannel = channel;
    this.flowData = null;
    this.resetStep();

    this.modal.querySelectorAll('.shopify-auth-tab').forEach(tab => {
      tab.classList.toggle('is-active', tab.getAttribute('data-channel') === channel);
    });

    const phoneWrap = this.modal.querySelector('.shopify-auth-phone-group');
    const emailInput = this.modal.querySelector('input[name="shopify_auth_email"]');
    const primaryInput = this.modal.querySelector('input[name="shopify_auth_value"]');
    const phoneCode = this.modal.querySelector('.shopify-auth-phone-code');

    if (channel === 'phone') {
      phoneWrap.style.display = 'flex';
      emailInput.style.display = 'none';
      emailInput.required = false;
      primaryInput.required = true;
      primaryInput.type = 'text';
      primaryInput.placeholder = 'رقم الجوال';
      phoneCode.style.display = 'flex';
      primaryInput.value = '';
    } else {
      phoneWrap.style.display = 'none';
      emailInput.style.display = 'block';
      emailInput.required = true;
      primaryInput.required = false;
      primaryInput.value = '';
    }
  }

  resetStep() {
    const otpWrap = this.modal.querySelector('.shopify-auth-otp-wrap');
    const secondaryWrap = this.modal.querySelector('.shopify-auth-secondary-wrap');
    const resend = this.modal.querySelector('.shopify-auth-resend');
    const submit = this.form.querySelector('.shopify-auth-submit');

    otpWrap.style.display = 'none';
    secondaryWrap.style.display = 'none';
    resend.style.display = 'none';
    resend.classList.remove('is-disabled');
    resend.textContent = 'إعادة إرسال';
    submit.textContent = 'إرسال الكود';
    submit.disabled = false;
    this.showError('');

    this.isStarting = false;
    this.isSubmitting = false;
    this.isResending = false;

    if (this.resendTimer) {
      clearInterval(this.resendTimer);
      this.resendTimer = null;
    }
  }

  async startFlow() {
    if (this.isStarting) return;
    this.isStarting = true;

    const submit = this.form.querySelector('.shopify-auth-submit');
    submit.disabled = true;

    try {
      const value = this.getPrimaryValue();
      if (!value) {
        this.showError('أدخل رقم الجوال أو البريد الإلكتروني');
        return;
      }

      const res = await fetch(this.base + '/start', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': this.csrfToken
        },
        body: JSON.stringify({
          channel: this.currentChannel,
          value: value,
          return_to: this.returnTo,
          source: this.source,
          cf_turnstile_response: await this.getTurnstileToken()
        })
      });
      this.resetTurnstile();

      if (res.status === 429) {
        this.showError('محاولات كثيرة، الرجاء المحاولة بعد قليل');
        return;
      }

      const json = await res.json();
      if (!json.success) {
        this.showError(json.message || 'فشل إرسال الكود');
        return;
      }

      this.flowData = json;
      this.form.querySelector('input[name="shopify_auth_return_to"]').value = this.returnTo;
      this.showOtpStep(json);
    } finally {
      this.isStarting = false;
      submit.disabled = false;
    }
  }

  showOtpStep(json) {
    const otpWrap = this.modal.querySelector('.shopify-auth-otp-wrap');
    const secondaryWrap = this.modal.querySelector('.shopify-auth-secondary-wrap');
    const resend = this.modal.querySelector('.shopify-auth-resend');
    const submit = this.form.querySelector('.shopify-auth-submit');

    otpWrap.style.display = 'block';
    resend.style.display = 'inline-block';
    submit.textContent = 'التحقق';
    this.startResendTimer();

    if (json.requires_additional_field) {
      secondaryWrap.style.display = 'block';
      const label = secondaryWrap.querySelector('.shopify-auth-secondary-label');
      const input = secondaryWrap.querySelector('input[name="secondary_value"]');
      if (json.additional_field === 'email') {
        label.textContent = 'البريد الإلكتروني';
        input.type = 'email';
        input.placeholder = 'example@example.com';
      } else {
        label.textContent = 'رقم الجوال';
        input.type = 'text';
        input.placeholder = '5xxxxxxxx';
      }
    } else {
      secondaryWrap.style.display = 'none';
    }
  }

  startResendTimer() {
    const resend = this.modal.querySelector('.shopify-auth-resend');
    if (!resend) return;

    this.resendEndsAt = Date.now() + (this.resendCooldown * 1000);
    resend.classList.add('is-disabled');
    this.updateResendText();

    if (this.resendTimer) {
      clearInterval(this.resendTimer);
    }

    this.resendTimer = setInterval(() => {
      const remaining = Math.max(0, Math.ceil((this.resendEndsAt - Date.now()) / 1000));
      this.updateResendText();

      if (remaining <= 0) {
        clearInterval(this.resendTimer);
        this.resendTimer = null;
        resend.classList.remove('is-disabled');
        resend.textContent = 'إعادة إرسال';
      }
    }, 1000);
  }

  updateResendText() {
    const resend = this.modal.querySelector('.shopify-auth-resend');
    if (!resend || !this.resendEndsAt) return;

    const remaining = Math.max(0, Math.ceil((this.resendEndsAt - Date.now()) / 1000));
    resend.textContent = remaining > 0 ? 'إعادة إرسال (' + remaining + ')' : 'إعادة إرسال';
  }

  async resendOtp() {
    const resend = this.modal.querySelector('.shopify-auth-resend');
    if (resend.classList.contains('is-disabled') || this.isResending) {
      return;
    }

    this.isResending = true;

    try {
      const value = this.getPrimaryValue();
      if (!value) return;

      const res = await fetch(this.base + '/resend', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': this.csrfToken
        },
        body: JSON.stringify({
          channel: this.currentChannel,
          value: value,
          return_to: this.returnTo,
          source: this.source,
          cf_turnstile_response: await this.getTurnstileToken()
        })
      });
      this.resetTurnstile();

      if (res.status === 429) {
        this.showError('محاولات كثيرة، الرجاء المحاولة بعد قليل');
      } else if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        this.showError(json.message || 'فشل إرسال الكود');
      }

      this.startResendTimer();
    } finally {
      this.isResending = false;
    }
  }

  async submitOtp() {
    if (this.isSubmitting) return;
    this.isSubmitting = true;

    const submit = this.form.querySelector('.shopify-auth-submit');
    submit.disabled = true;

    try {
      const otp = this.form.querySelector('input[name="otp_code"]').value.trim();
      const secondaryInput = this.form.querySelector('input[name="secondary_value"]');
      const secondaryVisible = secondaryInput && secondaryInput.parentElement.style.display !== 'none';
      const secondaryValue = secondaryVisible ? secondaryInput.value.trim() : null;

      if (!otp) {
        this.showError('أدخل كود التحقق');
        return;
      }

      const res = await fetch(this.base + '/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': this.csrfToken
        },
        body: JSON.stringify({
          channel: this.currentChannel,
          primary_value: this.getPrimaryValue(),
          otp_code: otp,
          secondary_value: secondaryValue,
          return_to: this.returnTo,
          source: this.source
        })
      });

      const json = await res.json();
      if (json.success) {
        window.location.href = json.login_url;
      } else {
        this.showError(json.message || 'فشل التحقق');
      }
    } finally {
      this.isSubmitting = false;
      submit.disabled = false;
    }
  }

  /**
   * Loads Turnstile once and renders the widget above the submit button.
   * Safe to call repeatedly; later calls reuse the same promise.
   */
  renderTurnstile() {
    if (this.turnstileReady) return this.turnstileReady;

    this.turnstileReady = new Promise((resolve) => {
      if (window.turnstile) return resolve();
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.onload = () => resolve();
      // Blocked script: send no token and let the server decide.
      script.onerror = () => resolve();
      document.head.appendChild(script);
    }).then(() => {
      if (!window.turnstile) return;
      const box = document.createElement('div');
      box.className = 'shopify-auth-turnstile';
      box.style.display = 'flex';
      box.style.justifyContent = 'center';

      this.form.querySelector('.shopify-auth-submit').before(box);
      this.turnstileWidgetId = window.turnstile.render(box, { sitekey: this.turnstileSiteKey });
    });

    return this.turnstileReady;
  }

  /**
   * Waits up to 10s for Cloudflare to issue a token (usually ~1s after the modal opens).
   */
  async getTurnstileToken() {
    await this.renderTurnstile();
    if (!window.turnstile || this.turnstileWidgetId === null) return '';

    for (let i = 0; i < 50 && !window.turnstile.getResponse(this.turnstileWidgetId); i++) {
      await new Promise(resolve => setTimeout(resolve, 200));
    }
    return window.turnstile.getResponse(this.turnstileWidgetId) || '';
  }

  /** Tokens are single-use, so get a fresh one ready for the next send. */
  resetTurnstile() {
    if (window.turnstile && this.turnstileWidgetId !== null) {
      window.turnstile.reset(this.turnstileWidgetId);
    }
  }

  getPrimaryValue() {
    if (this.currentChannel === 'phone') {
      const code = this.countryCode;
      let digits = this.form.querySelector('input[name="shopify_auth_value"]').value.trim().replace(/^0+/, '');
      if (!digits) return '';
      // Pasted/autofilled full number (e.g. 9665xxxxxxxx): drop the repeated country code.
      if (digits.startsWith(code) && digits.length > 10) digits = digits.slice(code.length);
      return '+' + code + digits;
    }
    return this.form.querySelector('input[name="shopify_auth_email"]').value.trim();
  }

  normalizeDigits(value) {
    const arabic = ['٠','١','٢','٣','٤','٥','٦','٧','٨','٩'];
    const english = ['0','1','2','3','4','5','6','7','8','9'];

    let normalized = value;
    arabic.forEach((digit, index) => {
      normalized = normalized.split(digit).join(english[index]);
    });

    return normalized;
  }

  showError(message) {
    this.form.querySelector('.shopify-auth-errors').innerHTML = message || '';
  }
}

window.ShopifyAuthModal = ShopifyAuthModal;

window.shopifyAuthModalInstance = window.shopifyAuthModalInstance || new ShopifyAuthModal();

function openShopifyAuthModal(e, options = {}) {
  e?.preventDefault();

  if (!window.shopifyAuthModalInstance) {
    window.shopifyAuthModalInstance = new ShopifyAuthModal(options);
  }

  window.shopifyAuthModalInstance.show({
    returnTo: options.returnTo || window.location.href,
    source: options.source || 'header'
  });

  return false;
}

// document.addEventListener('DOMContentLoaded', function () {
//   document.body.addEventListener('click', function (e) {
//     const target = e.target.closest('.m-signin-button');
//     const target2 = e.target.closest('.m-register-button');
//     if (!target && !target2) return;
//     e.preventDefault();
//     openShopifyAuthModal();
//   });
// });

// // أو عبر class event listener
// document.addEventListener('DOMContentLoaded', function () {
//   document.body.addEventListener('click', function (e) {
//     const is_visitor = document.querySelector('.is_visitor');
//     const target = e.target.closest('.shopify-payment-button');
//     if (!target || !is_visitor) return;
//     e.preventDefault();
//     openShopifyAuthModal();
//   });
// });
